import { ClerkProvider, SignedIn, SignedOut } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { AppLayout } from "./components/layout";
import { AdminRoute } from "./components/utils/admin-route";
import { FullAccessRoute } from "./components/utils/full-access-route";
import { ThemeProvider } from "./components/ui/theme-provider";
import { ProductsInitializer } from "./components/utils/products-initializer";
import { UserInitializer } from "./components/utils/user-initiazlier";
import SignInPage from "./pages/auth/sign-in-page";
import ClientsPage from "./pages/clients/clients-page";
import CustomersPage from "./pages/customers/customers-page";
import DashboardPage from "./pages/dashboard-page";
import DealsPage from "./pages/deals/deals-page";
import FinancePage from "./pages/operations/finance-page";
import InventoryPage from "./pages/operations/inventory-page";
import InventoryWorkspacePage from "./pages/operations/inventory-workspace-page";
import OperationsReportsPage from "./pages/operations/operations-reports-page";
import OrderSuggestionsPage from "./pages/operations/order-suggestions-page";
import PurchaseOrdersPage from "./pages/operations/purchase-orders-page";
import PaymentsPage from "./pages/operations/payments-page";
import BalancesPage from "./pages/operations/balances-page";
import NoteTemplatesPage from "./pages/operations/note-templates-page";
import SalesOrdersPage from "./pages/operations/sales-orders-page";
import ProductsPage from "./pages/products/products-page";
import BlockedContactsPage from "./pages/operations/blocked-contacts-page";
import PromoCodesPage from "./pages/promo-codes/promo-codes-page";
import VendorsPage from "./pages/vendors/vendors-page";
import MonthlyReportsPage from "./pages/reports/monthly-reports-page";
import ProductReportsPage from "./pages/reports/product-reports-page";
import DealsDetailsPage from "./pages/reports/deals-details-page";
import StockCheckGuidePage from "./pages/reports/stock-check-guide-page";
import InventoryReportsPage from "./pages/reports/inventory-reports-page";
import InventoryValuePage from "./pages/reports/inventory-value-page";
import PurchaseOrdersReportPage from "./pages/reports/purchase-orders-report-page";
import SalesReportPage from "./pages/reports/sales-report-page";
import { FirebaseTokenProvider } from "./utils/firebase-token-provider";

const queryClient = new QueryClient();

// Get Clerk publishable key from environment variables
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key");
}

function App() {
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
      <QueryClientProvider client={queryClient}>
        <FirebaseTokenProvider>
          <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
            <Router>
              <UserInitializer />
              <ProductsInitializer />
              <div className="min-h-screen bg-background">
                <Routes>
                  <Route
                    path="/sign-in"
                    element={
                      <SignedOut>
                        <SignInPage />
                      </SignedOut>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <DashboardPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/products"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <ProductsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/clients"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <ClientsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/leads"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <CustomersPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/deals"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <DealsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/order-suggestions"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <OrderSuggestionsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/purchase-orders"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <PurchaseOrdersPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/payments"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <PaymentsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/balances"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <FullAccessRoute>
                            <BalancesPage />
                          </FullAccessRoute>
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  {/* Legacy paths — the two inboxes are now one combined page. */}
                  <Route
                    path="/operations/paypal-payments"
                    element={
                      <Navigate to="/operations/payments" replace />
                    }
                  />
                  <Route
                    path="/operations/mercury-payments"
                    element={
                      <Navigate to="/operations/payments" replace />
                    }
                  />
                  <Route
                    path="/operations/inventory"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <InventoryPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/sales-orders"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <SalesOrdersPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/note-templates"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <AdminRoute>
                            <NoteTemplatesPage />
                          </AdminRoute>
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/finance"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <FinancePage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/inventory-workspace"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <InventoryWorkspacePage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/workspace/supply"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <Navigate to="/operations/inventory" replace />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/workspace/demand"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <Navigate to="/operations/inventory" replace />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/reports"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <OperationsReportsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <Navigate to="/operations/inventory" replace />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/operations/blocked-contacts"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <BlockedContactsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/promo-codes"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <PromoCodesPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/vendors"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <VendorsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/reports/products"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <ProductReportsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/reports/inventory"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <InventoryReportsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/reports/inventory-value"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <InventoryValuePage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/reports/purchase-orders"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <PurchaseOrdersReportPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/reports/sales"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <SalesReportPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/reports/monthly"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <MonthlyReportsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/reports/deals-details"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <DealsDetailsPage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/reports/stock-check-guide"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <StockCheckGuidePage />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <SignedIn>
                        <AppLayout>
                          <Navigate to="/reports/products" replace />
                        </AppLayout>
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/"
                    element={
                      <>
                        <SignedIn>
                          <Navigate to="/dashboard" replace />
                        </SignedIn>
                        <SignedOut>
                          <Navigate to="/sign-in" replace />
                        </SignedOut>
                      </>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </Router>
          </ThemeProvider>
        </FirebaseTokenProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ClerkProvider>
  );
}

export default App;
